import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import QRCodeStyling from 'qr-code-styling';
import { 
  Dialog, 
  Box, 
  Typography, 
  IconButton,
  Stack,
  Divider,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
// Import images (adjust paths as needed)
import panMask from '../../Assets/img/Kyc/PanCardMask.png';
import Lion from '../../Assets/img/Kyc/Lion.png';
import digi from '../../Assets/img/Kyc/digi2.jpg';
import logo from '../../Assets/img/default-logo.png';
import Flag from '../../Assets/img/Kyc/Flag.png';
import income_tax from '../../Assets/img/Kyc/income_tax.png';

const PanCard = ({ digiInfo, qrCodeUrl, websiteUrl, docType = "pan", token, forceFlip }) => {
    const [isPanCardVisible, setIsPanCardVisible] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const qrRef = useRef(null);
    const modalQrRef = useRef(null);
    const [qrCode, setQrCode] = useState(null);
    const [modalQrCode, setModalQrCode] = useState(null);

    const handlePanImageClick = () => {
        setIsPanCardVisible(!isPanCardVisible);
    };

    const handleQrClick = (e) => {
        e.stopPropagation();
        setShowQrModal(true);
    };

    const handleCloseModal = () => {
        setShowQrModal(false);
    };

    const getPublicDocViewUrl = () => {
        const authToken = token || localStorage.getItem("objectId");
        return `${window.location.origin}/public-docview?token=${encodeURIComponent(authToken)}&docType=${docType}`;
    };

    useEffect(() => {
        const qrUrl = getPublicDocViewUrl();
        if (qrUrl) {
            const qrCodeInstance = new QRCodeStyling({
                width: 120,
                height: 120,
                type: "canvas",
                data: qrUrl,
                image: logo,
                margin: 5,
                qrOptions: {
                    typeNumber: "0",
                    mode: "Byte",
                    errorCorrectionLevel: "H"
                },
                imageOptions: {
                    hideBackgroundDots: true,
                    imageSize: 0.3,
                    margin: 4,
                    crossOrigin: "anonymous",
                },
                dotsOptions: {
                    color: "#000000",
                    type: "rounded"
                },
                backgroundOptions: {
                    color: "#ffffff",
                },
                cornersSquareOptions: {
                    type: "extra-rounded",
                    color: "#000000"
                },
                cornersDotOptions: {
                    type: "dot",
                    color: "#000000"
                }
            });

            setQrCode(qrCodeInstance);
        }
    }, [qrCodeUrl, token, docType]);

    useEffect(() => {
        if (showQrModal) {
            const qrUrl = getPublicDocViewUrl() || qrCodeUrl;
            const modalQrInstance = new QRCodeStyling({
                width: 400,
                height: 400,
                type: "canvas",
                data: qrUrl,
                image: logo,
                margin: 10,
                qrOptions: {
                    typeNumber: "0",
                    mode: "Byte",
                    errorCorrectionLevel: "H"
                },
                imageOptions: {
                    hideBackgroundDots: true,
                    imageSize: 0.3,
                    margin: 10,
                    crossOrigin: "anonymous",
                },
                dotsOptions: {
                    color: "#000000",
                    type: "rounded"
                },
                backgroundOptions: {
                    color: "#ffffff",
                },
                cornersSquareOptions: {
                    type: "extra-rounded",
                    color: "#000000"
                },
                cornersDotOptions: {
                    type: "dot",
                    color: "#000000"
                }
            });

            setModalQrCode(modalQrInstance);
        }
    }, [showQrModal, qrCodeUrl, token, docType]);

    const qrRefCallback = useCallback((node) => {
        if (node && qrCode) {
            node.innerHTML = '';
            qrCode.append(node);
        }
    }, [qrCode]);

    const modalQrRefCallback = useCallback((node) => {
        if (node && modalQrCode) {
            node.innerHTML = '';
            modalQrCode.append(node);
        }
    }, [modalQrCode]);

    const publicUrl = getPublicDocViewUrl();

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    gap: "10px",
                    flexWrap: "wrap",
                    margin: "60px auto",
                    maxWidth: "90%",
                    width: "800px",
                    perspective: "1000px",
                }}
            >
                <Box
                    sx={{
                        flex: "1 1 auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transformStyle: "preserve-3d",
                        transition: "transform 0.6s",
                        transform: (isPanCardVisible || forceFlip) ? "rotateY(180deg)" : "rotateY(0deg)",
                        cursor: "pointer",
                        position: "relative",
                        maxWidth: "80%",
                        height: "100%",
                        padding: "10px",
                    }}
                    onClick={handlePanImageClick}
                >
                    {/* Front Side - Pan Mask */}
                    <Box
                        sx={{
                            position: "absolute",
                            backfaceVisibility: "hidden",
                            width: "100%",
                            marginTop: "60vh"
                        }}
                    >
                        <Box
                            component="img"
                            src={panMask}
                            alt="Pan Mask"
                            sx={{
                                border: "2px solid gray",
                                borderRadius: "15px",
                                width: "100%",
                                height: "72vh",
                            }}
                        />
                    </Box>

                    {/* Back Side - PAN Card */}
                    <Box
                        sx={{
                            position: "absolute",
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                            width: "100%",
                            marginTop: "60vh"
                        }}
                    >
                        <motion.div
                            initial={{ x: -300, opacity: 0 }}
                            animate={(isPanCardVisible || forceFlip) ? { x: 0, opacity: 1 } : { x: -300, opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            style={{
                                border: "2px solid gray",
                                borderRadius: "15px",
                                padding: "20px",
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                                backgroundColor: "white",
                            }}
                        >
                            {/* Header */}
                            <Stack
                                direction="row"
                                justifyContent="space-evenly"
                                alignItems="center"
                                flexWrap="wrap"
                                sx={{ rowGap: "10px" }}
                            >
                                <Box
                                    component="img"
                                    src={Lion}
                                    alt="Lion Logo"
                                    sx={{
                                        width: "15%",
                                        minWidth: "50px",
                                        objectFit: "contain"
                                    }}
                                />
                                <Box
                                    component="img"
                                    src={Flag}
                                    alt="Flag Logo"
                                    sx={{
                                        width: "20%",
                                        minWidth: "50px",
                                        objectFit: "contain"
                                    }}
                                />
                                <Box
                                    component="img"
                                    src={income_tax}
                                    alt="Income Tax Logo"
                                    sx={{
                                        width: "20%",
                                        minWidth: "50px",
                                        objectFit: "contain"
                                    }}
                                />
                            </Stack>

                            <Divider sx={{ borderColor: "blue" }} />

                            {/* Details Section */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    marginTop: "15px",
                                    justifyContent: "center"
                                }}
                            >
                                <Box
                                    sx={{
                                        textAlign: "left",
                                        flex: "1 1 auto",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Typography 
                                        variant="body1" 
                                        sx={{ 
                                            margin: "5px 0",
                                            fontWeight: "bold",
                                            fontSize: "16px"
                                        }}
                                    >
                                        PAN Card
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ margin: "5px 0" }}
                                    >
                                        <strong>Name:</strong> {digiInfo?.name || "N/A"}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ margin: "5px 0" }}
                                    >
                                        <strong>PAN Number:</strong>
                                        <Box component="span" sx={{ marginLeft: "5px" }}>
                                            {digiInfo?.pan_number || "N/A"}
                                        </Box>
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ margin: "5px 0" }}
                                    >
                                        <strong>DOB:</strong> {digiInfo?.dob || "N/A"}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ margin: "5px 0" }}
                                    >
                                        <strong>Gender:</strong> {digiInfo?.gender || "N/A"}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Divider />

                            <Typography 
                                variant="body2" 
                                sx={{ textAlign: "center", margin: "10px" }}
                            >
                                Digilocker | NEOIN
                            </Typography>

                            <Divider />

                            {/* Additional Details with QR Code */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                sx={{
                                    gap: "10px",
                                    marginTop: "10px",
                                    marginBottom: "10px",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={digi}
                                    alt="Digilocker Logo"
                                    sx={{
                                        width: "50%",
                                        minWidth: "120px",
                                        height: "auto",
                                        objectFit: "contain",
                                    }}
                                />

                                {/* QR Code Section with Logo in Center - Clickable */}
                                <Box
                                    onClick={handleQrClick}
                                    sx={{
                                        width: "40%",
                                        minWidth: "100px",
                                        position: "relative",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}
                                >
                                    {qrCodeUrl || publicUrl ? (
                                        <>
                                            <Box
                                                ref={qrRefCallback}
                                                sx={{
                                                    width: "80px",
                                                    height: "80px",
                                                    margin: "0 auto",
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    fontSize: "8px",
                                                    color: "#666",
                                                    marginTop: "5px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                Tap to scan
                                            </Typography>
                                        </>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: "80px",
                                                height: "80px",
                                                backgroundColor: "#f0f0f0",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border: "1px dashed #ccc",
                                            }}
                                        >
                                            <Typography 
                                                variant="caption" 
                                                sx={{ fontSize: "10px", color: "#999" }}
                                            >
                                                No QR
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>

                            <Divider sx={{ borderColor: "blue", padding: "10px" }} />

                            <Box sx={{ textAlign: "center", paddingBottom: "20px" }}>
                                <Typography variant="h6">
                                    स्थायी खाता संख्या कार्ड
                                </Typography>
                            </Box>
                        </motion.div>
                    </Box>
                </Box>
            </Box>

            {/* QR Code Modal */}
            <Dialog 
                open={showQrModal} 
                onClose={handleCloseModal} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        backgroundColor: "white",
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "30px",
                        backgroundColor: "white",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "gray",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography 
                        variant="h6" 
                        sx={{ 
                            mb: 3, 
                            textAlign: "center",
                            fontWeight: 600,
                            color: "#1e293b"
                        }}
                    >
                        Scan QR Code to View PAN Card
                    </Typography>

                    <Typography 
                        variant="body2" 
                        sx={{ 
                            mb: 3, 
                            textAlign: "center",
                            color: "#64748b"
                        }}
                    >
                        Use Digilocker or any QR scanner app
                    </Typography>

                    <Box
                        ref={modalQrRefCallback}
                        onClick={() => {
                            const publicUrl = getPublicDocViewUrl();
                            if (publicUrl) {
                                window.open(publicUrl, '_blank');
                            }
                        }}
                        sx={{
                            width: "350px",
                            height: "350px",
                            margin: "0 auto",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                        }}
                    />
                </Box>
            </Dialog>
        </>
    );
};

export default PanCard;